import React, { useState } from 'react'

import { useStaleBulkResolve } from '../hooks/useStaleBulkResolve'
import type { StaleResolveSkipped } from '../hooks/useStaleBulkResolve'
import { useLightMode } from '@/hooks/useLightMode'
import { useToasts } from '@/hooks/useToasts'
import { useTranslation } from 'react-i18next'

import { Modal, MultilineTextInput } from '@hivewatch/dark'

import {
  StaleResolveScope,
  StaleResolveSkipReason,
} from '@/graphql/generated/schemas'
import { tVar } from '@/utils/i18n'

const NOTE_MAX_LENGTH = 1_000

/** Long enough to read the per-reason skip counts (default 2s is not). */
const OUTCOME_TOAST_DURATION_MS = 6_000

interface StaleBulkResolveModalIProps {
  isOpen: boolean
  onClose: () => void
  /** Explicit ids for every scope; the backend never re-derives the set. */
  incidentIds: string[]
  scope: StaleResolveScope
  /** Attributed user's display name, for the PERSON title. */
  personName?: string
  /** For the PROGRAM title. */
  orgName?: string
  /** Called once after a successful mutation, with or without skips. */
  onResolved: () => void
}

/**
 * Confirmation for a stale bulk resolve (ENG-32272): the note-entry modal
 * doubles as the confirm step, so the title carries the explicit count and
 * scope. No real/false toggle; stale resolves are always RESOLVED only.
 * Outcomes are reported as toasts: a success toast for what resolved, plus a
 * warning toast summarizing skip counts by reason when the backend skipped
 * anything (resolved mid-flight, missing, or QuickReport access denied).
 */
export const StaleBulkResolveModal = ({
  isOpen,
  onClose,
  incidentIds,
  scope,
  personName,
  orgName,
  onResolved,
}: StaleBulkResolveModalIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const { showSuccess, showError, showWarning } = useToasts({
    colorScheme,
    isRebrand: true,
    toastDuration: OUTCOME_TOAST_DURATION_MS,
  })
  const { resolve, isResolving } = useStaleBulkResolve()

  const [note, setNote] = useState('')

  const handleClose = () => {
    if (isResolving) return
    setNote('')
    onClose()
  }

  /** "2 already resolved, 1 without Quick Report access" style summary. */
  const buildSkipReasonSummary = (skipped: StaleResolveSkipped[]) => {
    const reasonKeys: Array<[StaleResolveSkipReason, string]> = [
      [
        StaleResolveSkipReason.AlreadyResolved,
        'incidents:stale_bulk_resolve_reason_already_resolved',
      ],
      [
        StaleResolveSkipReason.AccessDenied,
        'incidents:stale_bulk_resolve_reason_access_denied',
      ],
      [
        StaleResolveSkipReason.NotFound,
        'incidents:stale_bulk_resolve_reason_not_found',
      ],
    ]
    return reasonKeys
      .map(([reason, key]) => {
        const count = skipped.filter((skip) => skip.reason === reason).length
        return count > 0 ? tVar(t, key, { count }) : null
      })
      .filter(Boolean)
      .join(', ')
  }

  const handleResolve = async () => {
    try {
      const result = await resolve({ incidentIds, message: note, scope })
      // Skipped or not, incidents were resolved; the caller refetches now.
      onResolved()
      setNote('')
      onClose()
      if (result.resolvedCount > 0) {
        showSuccess(
          tVar(t, 'incidents:stale_bulk_resolve_success', {
            count: result.resolvedCount,
          })
        )
      }
      if (result.skipped.length > 0) {
        showWarning(
          tVar(t, 'incidents:stale_bulk_resolve_skipped_toast', {
            count: result.skipped.length,
            reasons: buildSkipReasonSummary(result.skipped),
          })
        )
      }
    } catch {
      // Already reported to Sentry (with analytics) by useStaleBulkResolve.
      showError(t('incidents:stale_bulk_resolve_error'))
    }
  }

  const getTitle = () => {
    switch (scope) {
      case StaleResolveScope.Person:
        return tVar(t, 'incidents:stale_bulk_resolve_title_person', {
          count: incidentIds.length,
          name: personName ?? '',
        })
      case StaleResolveScope.Program:
        return tVar(t, 'incidents:stale_bulk_resolve_title_program', {
          count: incidentIds.length,
          orgName: orgName ?? '',
        })
      default:
        return tVar(t, 'incidents:stale_bulk_resolve_title_selected', {
          count: incidentIds.length,
        })
    }
  }

  return (
    <Modal
      // The in-progress note would be lost on an accidental Escape or
      // overlay click (same protection as the incidents-table bulk modal).
      closeOnEsc={false}
      closeOnOverlayClick={false}
      colorScheme={colorScheme}
      isOpen={isOpen}
      isSaveLoading={isResolving}
      maxW='480px'
      onClose={handleClose}
      onSave={handleResolve}
      saveButtonLabel={t('incidents:stale_bulk_resolve_confirm')}
      showCancelButton={true}
      size='compact'
      title={getTitle()}
    >
      <MultilineTextInput
        colorScheme={colorScheme}
        data-testid='stale-bulk-resolve-note'
        // The labeled wrapper's side border rails read as stray lines
        // inside a modal; drop them and their inset padding.
        hasSideBorders={false}
        isDisabled={isResolving}
        label={t('incidents:stale_bulk_resolve_note_label')}
        maxLength={NOTE_MAX_LENGTH}
        onChange={(event) => setNote(event.target.value)}
        outerPadding='0'
        placeholder={t('incidents:stale_bulk_resolve_note_placeholder')}
        value={note}
      />
    </Modal>
  )
}
